import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, Calculator, DollarSign, Percent, TrendingDown
} from "lucide-react";
import { ChatToggleButton } from "@/components/AIChatWidget";
import axios from "axios";
import { toast } from "sonner";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";

const MortgageCalculator = () => {
  const navigate = useNavigate();
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);
  
  const [formData, setFormData] = useState({
    loan_amount: 300000,
    interest_rate: 7,
    loan_term_years: 30,
    down_payment: 60000,
    property_taxes_annual: 3600,
    insurance_annual: 1800,
    pmi_monthly: 0
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setResult(null);
  };

  const calculateMortgage = async () => {
    setCalculating(true);
    try {
      const response = await axios.post(`${API}/calculator/mortgage`, formData);
      setResult(response.data);
      toast.success("Mortgage calculated!");
    } catch (error) {
      toast.error("Failed to calculate mortgage");
    } finally {
      setCalculating(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  // Prepare yearly data for chart
  const getYearlyData = () => {
    if (!result?.amortization_schedule) return [];
    const yearly = [];
    for (let year = 1; year <= formData.loan_term_years; year++) {
      const yearData = result.amortization_schedule.filter(m => m.year === year);
      if (yearData.length > 0) {
        yearly.push({
          year,
          principal: yearData.reduce((sum, m) => sum + m.principal, 0),
          interest: yearData.reduce((sum, m) => sum + m.interest, 0),
          balance: yearData[yearData.length - 1].balance
        });
      }
    }
    return yearly;
  };

  return (
    <div className="min-h-screen bg-slate-900" data-testid="mortgage-calculator-page">
      {/* Header */}
      <header className="glass border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate("/dashboard")}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
              <div className="h-6 w-px bg-slate-700"></div>
              <h1 className="text-lg font-semibold text-white font-['Manrope']">Mortgage Calculator</h1>
            </div>
            <div className="flex items-center gap-3">
              <ChatToggleButton />
              <Button 
              onClick={calculateMortgage}
              disabled={calculating}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
              data-testid="calculate-mortgage-btn"
            >
              {calculating ? (
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-slate-900 mr-2"></div>
              ) : (
                <Calculator className="h-4 w-4 mr-2" />
              )}
              Calculate
            </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Form */}
          <div className="space-y-6">
            <div className="glass-card rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-amber-500" />
                Loan Details
              </h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-300">Home Price</Label>
                  <Input
                    type="number"
                    value={formData.loan_amount + formData.down_payment}
                    onChange={(e) => {
                      const price = parseFloat(e.target.value) || 0;
                      handleInputChange('loan_amount', price - formData.down_payment);
                    }}
                    className="bg-slate-950 border-slate-800 text-white font-mono"
                    data-testid="home-price-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Down Payment</Label>
                  <Input
                    type="number"
                    value={formData.down_payment}
                    onChange={(e) => handleInputChange('down_payment', parseFloat(e.target.value) || 0)}
                    className="bg-slate-950 border-slate-800 text-white font-mono"
                  />
                  <div className="text-xs text-slate-500 mt-1">
                    {((formData.down_payment / (formData.loan_amount + formData.down_payment)) * 100).toFixed(1)}% down
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300">Loan Amount</Label>
                  <Input
                    type="number"
                    value={formData.loan_amount}
                    onChange={(e) => handleInputChange('loan_amount', parseFloat(e.target.value) || 0)}
                    className="bg-slate-950 border-slate-800 text-white font-mono"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Interest Rate (%)</Label>
                    <Input
                      type="number"
                      step="0.125"
                      value={formData.interest_rate}
                      onChange={(e) => handleInputChange('interest_rate', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Loan Term</Label>
                    <Select 
                      value={formData.loan_term_years.toString()} 
                      onValueChange={(v) => handleInputChange('loan_term_years', parseInt(v))}
                    >
                      <SelectTrigger className="bg-slate-950 border-slate-800 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800 max-h-60">
                        <SelectItem value="10">10 Years</SelectItem>
                        <SelectItem value="15">15 Years</SelectItem>
                        <SelectItem value="20">20 Years</SelectItem>
                        <SelectItem value="25">25 Years</SelectItem>
                        <SelectItem value="30">30 Years</SelectItem>
                        <SelectItem value="35">35 Years</SelectItem>
                        <SelectItem value="40">40 Years</SelectItem>
                        <SelectItem value="50">50 Years</SelectItem>
                        <SelectItem value="75">75 Years</SelectItem>
                        <SelectItem value="100">100 Years</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>

            <div className="glass-card rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Percent className="h-5 w-5 text-cyan-500" />
                Additional Costs
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Property Taxes/Year</Label>
                    <Input
                      type="number"
                      value={formData.property_taxes_annual}
                      onChange={(e) => handleInputChange('property_taxes_annual', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Insurance/Year</Label>
                    <Input
                      type="number"
                      value={formData.insurance_annual}
                      onChange={(e) => handleInputChange('insurance_annual', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300">PMI/Month (if applicable)</Label>
                  <Input
                    type="number"
                    value={formData.pmi_monthly}
                    onChange={(e) => handleInputChange('pmi_monthly', parseFloat(e.target.value) || 0)}
                    className="bg-slate-950 border-slate-800 text-white font-mono"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="space-y-6">
            {!result ? (
              <div className="glass-card rounded-xl p-12 text-center h-full flex flex-col items-center justify-center">
                <Calculator className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Enter Loan Details</h3>
                <p className="text-slate-400 mb-6">Fill in the form and click Calculate to see your mortgage breakdown</p>
                <Button 
                  onClick={calculateMortgage}
                  className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                >
                  <Calculator className="h-4 w-4 mr-2" />
                  Calculate Now
                </Button>
              </div>
            ) : (
              <>
                {/* Monthly Payment */}
                <div className="bg-gradient-to-br from-amber-500/20 to-amber-500/5 border border-amber-500/30 rounded-xl p-6">
                  <div className="text-sm text-slate-300 mb-2">Monthly Payment</div>
                  <div className="text-4xl font-bold font-mono text-amber-500" data-testid="monthly-payment">
                    {formatCurrency(result.monthly_payment)}
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-slate-400">Principal & Interest</div>
                      <div className="font-mono text-white">
                        {formatCurrency(result.monthly_payment - formData.property_taxes_annual/12 - formData.insurance_annual/12 - formData.pmi_monthly)}
                      </div>
                    </div>
                    <div>
                      <div className="text-slate-400">Taxes & Insurance</div>
                      <div className="font-mono text-white">
                        {formatCurrency(formData.property_taxes_annual/12 + formData.insurance_annual/12)}
                      </div>
                    </div>
                    {formData.pmi_monthly > 0 && (
                      <div>
                        <div className="text-slate-400">PMI</div>
                        <div className="font-mono text-white">{formatCurrency(formData.pmi_monthly)}</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Summary Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="metric-card">
                    <div className="text-sm text-slate-400 mb-1">Total Payment</div>
                    <div className="text-2xl font-bold font-mono text-white">
                      {formatCurrency(result.total_payment)}
                    </div>
                  </div>
                  <div className="metric-card">
                    <div className="text-sm text-slate-400 mb-1">Total Interest</div>
                    <div className="text-2xl font-bold font-mono text-red-400">
                      {formatCurrency(result.total_interest)}
                    </div>
                  </div>
                </div>

                {/* Loan Balance Chart */}
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <TrendingDown className="h-5 w-5 text-cyan-500" />
                    Loan Balance Over Time
                  </h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={getYearlyData()}>
                        <defs>
                          <linearGradient id="balanceGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="year" stroke="#64748b" />
                        <YAxis stroke="#64748b" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                          labelStyle={{ color: '#f1f5f9' }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="balance" 
                          stroke="#06b6d4" 
                          fill="url(#balanceGradient)"
                          strokeWidth={2}
                          name="Remaining Balance"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Amortization Table (First 5 Years) */}
                <div className="glass-card rounded-xl overflow-hidden">
                  <div className="p-4 border-b border-slate-800">
                    <h3 className="text-lg font-semibold text-white">Amortization Schedule (First 5 Years)</h3>
                  </div>
                  <div className="overflow-x-auto max-h-80">
                    <table className="w-full data-table">
                      <thead className="sticky top-0 bg-slate-950">
                        <tr>
                          <th>Month</th>
                          <th>Payment</th>
                          <th>Principal</th>
                          <th>Interest</th>
                          <th>Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.amortization_schedule.slice(0, 60).map((row, index) => (
                          <tr key={index}>
                            <td>{row.month}</td>
                            <td className="font-mono">{formatCurrency(row.payment)}</td>
                            <td className="font-mono text-emerald-500">{formatCurrency(row.principal)}</td>
                            <td className="font-mono text-red-400">{formatCurrency(row.interest)}</td>
                            <td className="font-mono">{formatCurrency(row.balance)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default MortgageCalculator;
